import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../header/header';
import { constantStyles } from '../constants/constantStyles';
import Filters from './filters';
import SortDropdown from './sortDropdown';
import { FaRegHeart, FaHeart } from "react-icons/fa";
import { ToastContainer, toast } from 'react-toastify';
import { useMediaQuery } from 'react-responsive';
import LoadingScreen from '../utils/loadingState';
import ImageSpinner from '../utils/imageSpinner';
import { useAuth } from '../auth/AuthContext';
import { useIsAdmin } from '../auth/useAdmin';
import { AdminProductModifiers, AdminProductTimestamps } from './AdminProductControls';

const endpoint = process.env.NODE_ENV === 'production' ? process.env.REACT_APP_SERVER_URL : process.env.REACT_APP_LOCAL_SERVER_URL;

// Helper function to generate SEO metadata based on page context
const generateSEOMetadata = (page_title, searchFilters, availableCategories, seoOverrides = {}) => {
    const currentPath = window.location.pathname;
    let title = '';
    let description = '';
    let url = `https://thesidewalk.co${currentPath}`;

    // Start with provided SEO data or fallback
    if (seoOverrides.title && seoOverrides.description) {
        title = seoOverrides.title;
        description = seoOverrides.description;
    } else {
        // Ultimate fallback if no seo prop provided
        title = `${page_title} | The Sidewalk`;
        description = `Shop ${page_title.toLowerCase()} from the best emerging fashion brands.`;
    }

    // Enhance with category filters if present
    const categoryNames = [];
    if (availableCategories && availableCategories.length > 0 && searchFilters.categories) {
        console.log('availableCategories for SEO:', availableCategories, searchFilters.categories);
        const flattenCategories = (cats) => {
            // Safety check: ensure cats is an array
            if (!Array.isArray(cats)) {
                return [];
            }
            let result = [];
            cats.forEach(cat => {
                result.push(cat);
                if (cat.children && Array.isArray(cat.children)) {
                    result = result.concat(flattenCategories(cat.children));
                }
            });
            return result;
        };

        // Get the categories array - availableCategories[0] is an object with children property
        const categoriesData = availableCategories[0];
        let allCategories = [];

        if (categoriesData) {
            // If it's an object with children, flatten the children
            if (categoriesData.children && Array.isArray(categoriesData.children)) {
                allCategories = flattenCategories(categoriesData.children);
            }
            // If it's already an array, flatten it directly
            else if (Array.isArray(categoriesData)) {
                allCategories = flattenCategories(categoriesData);
            }
        }

        const selectedCategoryIds = Array.isArray(searchFilters.categories)
            ? searchFilters.categories
            : [searchFilters.categories];

        selectedCategoryIds.forEach(id => {
            const category = allCategories.find(cat => cat.id === id);
            if (category) {
                categoryNames.push(category.name);
            }
        });
    }

    // If we have category filters, enhance the title/description
    if (categoryNames.length > 0) {
        // Enhance title by replacing the ending
        title = title.replace(' | The Sidewalk', `'s ${categoryNames.join(', ')} | The Sidewalk`);

        // Enhance description - check if it starts with "from" (partial description pattern)
        if (description.startsWith('from ')) {
            description = `Shop ${categoryNames.join(', ').toLowerCase()} ${description}`;
        } else {
            // Full description - prepend category context
            description = `${categoryNames.join(', ')}: ${description}`;
        }
    }

    return { title, description, url };
};


const storeReaction = (product_id, action_type) => {
    fetch(`${endpoint}api/product-reaction`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ product_id, action_type }),
    });
}

const updateLikes = (productId, setLikeState) => {

    let likedProducts = JSON.parse(localStorage.getItem('likedProducts')) || [];
    let dispatchAction;

    if (likedProducts.includes(productId)) {
        likedProducts = likedProducts.filter(id => id !== productId);
        setLikeState(false);
        dispatchAction = 'REMOVE';
        storeReaction(productId, 'liked_off');
    } else {
        likedProducts.push(productId);
        setLikeState(true);
        dispatchAction = 'ADD';
        storeReaction(productId, 'liked_on');
    }

    localStorage.setItem('likedProducts', JSON.stringify(likedProducts));
    window.dispatchEvent(new CustomEvent("likesUpdated", { detail: { action: dispatchAction } }));
};

const isProductLiked = (productId) => {
    let likedProducts = JSON.parse(localStorage.getItem('likedProducts')) || [];
    return likedProducts.includes(productId);
}

const loadedImages = new Set();

const ProductPage = ({ page_title, initial_filters, likesOnly, seo }) => {
    const { getAccessToken } = useAuth();
    const isAdmin = useIsAdmin();

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [productsError, setProductsError] = useState(false);

    const [availableCategories, setAvailableCategories] = useState([]);
    const [availableBrands, setAvailableBrands] = useState([]);
    const [priceRange, setPriceRange] = useState({});

    const [startingParams, setStartingParams] = useState({});
    const [searchFilters, setSearchFilters] = useState({ ...initial_filters });
    const [readyToFetch, setReadyToFetch] = useState(false);

    // Determine initial sort: default to 'relevance' for search queries,
    // default to 'newest' for regular browsing
    const urlParams = new URLSearchParams(window.location.search);
    const hasSearchQuery = urlParams.has('query');
    const [sortBy, setSortBy] = useState(hasSearchQuery ? 'relevance' : 'newest');

    const [reachedEndOfData, setReachedEndOfData] = useState(false);
    const [totalCount, setTotalCount] = useState(null);

    const isMobile = useMediaQuery({ maxWidth: constantStyles.mobileWidth });

    // Check if this is the stale products page (admin-only)
    const isStaleMode = initial_filters?.stale === true;

    useEffect(() => {
        return () => {
            loadedImages.clear();
        };
    }, []);

    useEffect(() => {
        // Read and preserve search query BEFORE processing params
        const urlParams = new URLSearchParams(window.location.search);
        const searchQuery = urlParams.get('query');

        let newStartingParams = new URLSearchParams(window.location.search);
        newStartingParams = Object.fromEntries(newStartingParams.entries());

        // Remove query from params that get converted to arrays
        delete newStartingParams.query;

        for (let key in newStartingParams) {
            if (!Array.isArray(newStartingParams[key])) {
                if (!isNaN(Number(newStartingParams[key]))) {
                    newStartingParams[key] = [Number(newStartingParams[key])];
                } else {
                    newStartingParams[key] = [newStartingParams[key]];
                }
            }
        }

        setStartingParams(newStartingParams);
        setSearchFilters((prev) => { return { ...prev, ...newStartingParams } });

        // Clear URL parameters but preserve the search query if it exists
        const newUrl = searchQuery
            ? `${window.location.pathname}?query=${encodeURIComponent(searchQuery)}`
            : window.location.pathname;
        window.history.replaceState({}, '', newUrl);

        setReadyToFetch(true);
    }, []);

    const [filteredPrice, setFilteredPrice] = useState({});

    const [page, setPage] = useState(0);
    const observer = useRef();

    const showGenders = !('gender' in initial_filters);

    const loadingProductsErrorText = "Sorry we are having trouble finding products, please try again later!";
    const noProductsFound = "We found no products based on your search, please try something different!";
    const noLikesMessage = "It seems like you have not liked any products yet!";

    const fetchData = async (searchFilters, filteredPrice, page, sortParam) => {
        if (!readyToFetch) {
            return;
        }
        try {
            if (likesOnly && !localStorage.getItem('likedProducts')) {
                setLoading(false);
                return;
            }
            const currentUrl = new URL(window.location.href);
            const currentParams = new URLSearchParams(currentUrl.search);


            const paramsObject = {};

            // Loop through each search parameter
            currentParams.forEach((value, key) => {
                // If the key already exists in the object, push the value into an array
                if (paramsObject[key]) {
                    paramsObject[key] = Array.isArray(paramsObject[key])
                        ? [...paramsObject[key], value] // If it's already an array, push the new value
                        : [paramsObject[key], value];  // Convert to array if it's not already
                } else {
                    // If the key doesn't exist, just assign the value
                    paramsObject[key] = value;
                }
            });

            // Use different endpoint for stale products
            const apiPath = isStaleMode ? 'api/products/stale' : 'api/products';
            const url = new URL(`${endpoint}${apiPath}`);

            // console.log('url.searchParams', Object.fromEntries(url.searchParams));
            // console.log('paramsObject', paramsObject);
            // console.log('searchFilters', searchFilters);
            Object.keys(paramsObject).forEach(key => url.searchParams.append(key, paramsObject[key]));
            // Don't include 'stale' in query params for the stale endpoint (it's in the path)
            // Don't include 'query' as it's already in paramsObject, not a filter
            Object.keys(searchFilters).forEach(key => {
                if (key !== 'stale' && key !== 'query') {
                    url.searchParams.append(key, searchFilters[key]);
                }
            });
            Object.keys(filteredPrice).forEach(key => url.searchParams.append(key, filteredPrice[key]));
            url.searchParams.append('page', page);

            url.searchParams.append('sort', sortParam);

            likesOnly && url.searchParams.append('likes', JSON.parse(localStorage.getItem('likedProducts')) || []);

            // Add auth token when user is admin (so backend includes admin fields like categories)
            const fetchOptions = {};
            if (isAdmin) {
                const token = await getAccessToken();
                fetchOptions.headers = {
                    Authorization: `Bearer ${token}`,
                };
            }

            const response = await fetch(url, fetchOptions);
            const result = await response.json();

            if (result.error) {
                throw new Error(result.error);
            }
            if (page === 0) {
                setData(result.products);
            } else {
                setData((prevProducts) => [...prevProducts, ...result.products]);
            }

            setReachedEndOfData(result.products.length === 0);

            // Update total count if provided (only on page 0)
            if (result.total_count !== null && result.total_count !== undefined) {
                setTotalCount(result.total_count);
            }

            setAvailableCategories([result.categories]);
            setPriceRange(result.price_range);
            setAvailableBrands([result.brands]);

            setLoading(false);

        } catch (err) {
            setProductsError(true);
            setLoading(false);
        }
    };

    useEffect(() => {
        const seenNoticeBefore = localStorage.getItem('manuallyClosedNotice');
        if (!seenNoticeBefore) {
            toast.info('We may make a small commission when you buy from one of our affiliates!', {
                position: "bottom-left",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: false,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
                onClose: (removedByUser) => { if (removedByUser) { localStorage.setItem('manuallyClosedNotice', true); } }
            });
        }
    }, []);

    useEffect(() => {
        setPage(0);
        setLoading(true);  // Show loading indicator when filters change
        fetchData(searchFilters, filteredPrice, 0, sortBy);
    }, [searchFilters, filteredPrice, sortBy]);

    useEffect(() => {
        if (page > 0) {
            setLoading(true);
            fetchData(searchFilters, filteredPrice, page, sortBy);
        }
    }, [page]);

    const lastPostElementRef = useCallback(
        (node) => {
            if (loading || reachedEndOfData) return;
            if (observer.current) observer.current.disconnect();

            observer.current = new IntersectionObserver((entries) => {
                if (entries[0].isIntersecting) {
                    setPage((prevPage) => prevPage + 1);
                }
            });

            if (node) observer.current.observe(node);
        },
        [loading, reachedEndOfData]
    );

    const ProductContents = ({ item }) => {

        const { product_id, product_title, price, sale_price } = item;

        const [like, setLike] = useState(isProductLiked(product_id));

        const priceDiv = sale_price ?
            <div style={styles.twoPrices}>
                <div style={styles.oldPrice}>{price}</div>
                <div style={styles.salePrice}>{sale_price}</div>
            </div>
            :
            <div style={styles.price}>{price}</div>

        return (<div style={styles.productContents}>
            <div style={styles.leftProductContents}>
                <div style={styles.title}>{product_title}</div>
                {priceDiv}
            </div>
            <div style={styles.rightProductContents}>
                <div style={styles.like} onClick={(e) => {
                    updateLikes(product_id, setLike);
                    e.stopPropagation();
                }}>
                    {like ? <FaHeart style={{ ...styles.heart, ...styles.colorHeart }} /> : <FaRegHeart style={styles.heart} />}</div>
            </div>
        </div>);
    };
    const ProductCard = ({ item, index, productStyling }) => {
        const { product_url, product_id, image_url, product_title } = item;
        const [imgError, setImgError] = useState(false);
        const [imgLoading, setImgLoading] = useState(!loadedImages.has(product_id));
        const [adminActionLoading, setAdminActionLoading] = useState(false);

        if (imgError) return null;

        const handleImageLoad = () => {
            loadedImages.add(product_id);
            setImgLoading(false);
        }


        return (
            <div
                style={{ ...styles.productContainer, ...productStyling }}
                onClick={() => {
                    fetch(`${endpoint}api/product-click`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ product_id }),
                    });
                    window.open(product_url, "_blank");
                }}
                ref={index === data.length - 1 ? lastPostElementRef : null}
            >
                <div style={{
                    ...styles.imageContainer,
                    position: "relative",
                    overflow: "hidden",
                    ...(imgLoading ? { minHeight: isMobile ? 275 : 450 } : {}),
                }}>
                    {(imgLoading || adminActionLoading) && (
                        <div
                            style={{
                                ...styles.image,
                                ...styles.imageLoadingState
                            }}
                        >
                            <div style={styles.imageSpinner}>
                                <ImageSpinner />
                            </div>
                        </div>
                    )}
                    <img
                        src={image_url}
                        alt={product_title}
                        style={{
                            ...styles.image,
                        }}
                        onLoad={handleImageLoad}
                        onError={() => setImgError(true)}
                    />
                </div>
                {isAdmin && <AdminProductTimestamps created_at={item.created_at} last_updated_at={item.last_updated_at} />}
                <ProductContents item={item} />
                {isAdmin && <AdminProductModifiers item={item} availableCategories={availableCategories} isLoading={adminActionLoading} setIsLoading={setAdminActionLoading} />}
            </div>
        );
    };

    const products = (data) => {


        const fullWidth = 300;
        const minWidth = 150;
        const productPadding = 10;
        const halfScreenWithPadding = window.innerWidth / 2 - (2 * productPadding);
        const displayWidth = isMobile ? Math.max(minWidth, Math.min(fullWidth, halfScreenWithPadding)) : 300;

        const productsStyling = { gridTemplateColumns: 'repeat(auto-fit, minmax(' + (displayWidth + 2 * productPadding) + 'px, 1fr))' };

        const productStyling = { width: displayWidth + 'px', padding: productPadding + 'px', }


        return <div style={{ ...styles.productsContainer, ...productsStyling }}>
            {data.map((item, index) => {
                return (<ProductCard
                    key={item.product_id}
                    index={index}
                    item={item}
                    productStyling={productStyling}
                />)
            })}
        </div>;
    }
    const sortDropDownElement = <SortDropdown onSortChange={setSortBy} currentSort={sortBy} />;

    const filtersElement = <Filters
        availableCategories={availableCategories} setSearchFilters={setSearchFilters}
        priceRange={priceRange} setFilteredPrice={setFilteredPrice} filteredPrice={filteredPrice}
        availableBrands={availableBrands}
        showGenders={showGenders}
        startingParams={startingParams}
        isMobile={isMobile}
        sortDropDown={sortDropDownElement}
        isLoading={loading && data.length > 0}
    />;

    const DataElement = ({ }) => {

        const productsIssuesStyle = { ...styles.productsIssue, ...(!isMobile ? { transform: 'translateX(-135px)' } : {}) };

        if (loading && data.length === 0) return <div style={productsIssuesStyle}><LoadingScreen /></div>;
        if (productsError) return <div style={productsIssuesStyle}>{loadingProductsErrorText}</div>;
        if (data.length === 0 && likesOnly) return <div style={productsIssuesStyle}>{noLikesMessage}</div>;
        if (data.length === 0) return <div style={productsIssuesStyle}>{noProductsFound}</div>;

        return products(data);
    }
    const topSection = () => {
        // Format title with total count if available
        const formattedTitle = totalCount !== null
            ? `${page_title} (${totalCount.toLocaleString()} ${totalCount === 1 ? 'result' : 'results'})`
            : page_title;

        return isMobile ?
            <div style={styles.topSection}>
                <div style={styles.pageTitle}>{formattedTitle}</div>
                <div style={{ ...(data.length > 0 ? {} : { 'visibility': 'hidden' }) }}>
                    {filtersElement}
                </div>
            </div>
            : (
                <div style={styles.topSection}>
                    <div style={styles.titleAndSpinnerContainer}>
                        <div style={styles.pageTitle}>{formattedTitle}</div>
                        {loading && data.length > 0 && (
                            <div style={styles.titleSpinner}>
                                <ImageSpinner overrideSizeStyles={{width: '120px'}} extraStyles={{marginLeft: '-40px'}}/>
                            </div>
                        )}
                    </div>
                    {data.length > 0 && sortDropDownElement}
                </div>
            );
    };

    // Generate SEO metadata (seo prop provides base title/description, we enhance with filters/search)
    const seoMetadata = generateSEOMetadata(page_title, searchFilters, availableCategories, seo);

    return (
        <div>
            <Helmet>
                <title>{seoMetadata.title}</title>
                <meta name="description" content={seoMetadata.description} />
                <meta property="og:title" content={seoMetadata.title} />
                <meta property="og:description" content={seoMetadata.description} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={seoMetadata.url} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={seoMetadata.title} />
                <meta name="twitter:description" content={seoMetadata.description} />
                <link rel="canonical" href={seoMetadata.url} />
            </Helmet>
            <Header />
            {topSection()}
            <div style={styles.productFilterContainer}>
                <div style={{ ...(data.length > 0 ? {} : { 'visibility': 'hidden' }) }}>
                    {!isMobile && filtersElement}
                </div>
                <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <DataElement />
                    {loading && data.length > 0 && (
                        <div style={styles.loadingMoreContainer}>
                            <ImageSpinner />
                        </div>
                    )}
                </div>
                {data.length > 0 && <ToastContainer
                    position="bottom-left"
                    newestOnTop={false}
                    closeOnClick={false}
                    rtl={false}
                    draggable
                    theme="light"
                />}
            </div>
        </div>
    );
};

export default ProductPage;

const styles = {
    topSection: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'relative',
    },
    titleAndSpinnerContainer: {
        display: 'flex',
        alignItems: 'center',
    },
    titleSpinner: {
        display: 'flex',
        alignItems: 'center',
        opacity: 0.6,
        transform: 'scale(0.6)',
    },
    filterAndSortContainer: {
        display: 'flex',
        paddingBottom: '10px',
        justifyContent: 'space-around',
    },
    pageTitle: {
        fontFamily: constantStyles.mainFontFamily,
        margin: constantStyles.outermostMargin,
        marginTop: '20px',
        marginBottom: '20px',
        marginRight: '0px',
        fontSize: '24px',
    },
    imageLoadingState: {
        backgroundSize: "cover",
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "rgba(240, 240, 240, 0.8)",
    },
    imageSpinner: {
        opacity: 0.5,
    },
    image: {
        display: 'block',
        height: 'auto',
        width: '100%',
    },
    imageContainer: {
    },
    productContents: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        padding: '10px 0',
    },
    rightProductContents: {
        display: 'flex',
        marginLeft: '7%',
    },
    leftProductContents: {
        display: 'flex',
        flexDirection: 'column',
    },
    productContainer: {
        cursor: 'pointer',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
    },
    productsContainer: {
        display: 'inline-grid',
        flexGrow: '1',
        alignItems: 'start',
    },
    productFilterContainer: {
        display: 'flex',
    },
    productsIssue: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: '10%',
        width: '100%',
    },
    title: {
        maxHeight: '2em',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontFamily: constantStyles.mainFontFamily,
    },
    colorHeart: {
        color: '#ff0000b8',
    },
    heart: {
        fontSize: "24px",
    },
    oldPrice: {
        textDecoration: "line-through",
    },
    salePrice: {
        color: constantStyles.saleRed,
    },
    twoPrices: {
        display: 'flex',
        gap: '10px',
    },
    loadingMoreContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '40px 0',
        width: '100%',
        opacity: 0.7,
    },
};